import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { BackCoachClient, components } from "@backcoach/api-client";

/**
 * The client context. Every screen reads through the generated client; the
 * extension and the console decide where it points and how it authenticates.
 */
export type Schemas = components["schemas"];

const ClientContext = createContext<BackCoachClient | null>(null);

export function ApiProvider({ client, children }: { client: BackCoachClient; children: ReactNode }) {
  return <ClientContext.Provider value={client}>{children}</ClientContext.Provider>;
}

export function useApi(): BackCoachClient {
  const c = useContext(ClientContext);
  if (!c) throw new Error("useApi outside ApiProvider");
  return c;
}

export interface Resource<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/**
 * A small fetch-and-hold hook. No cache library: the screens are few, the
 * data is small, and a reload after a write is explicit rather than magic.
 */
export function useResource<T>(load: (api: BackCoachClient) => Promise<T>, deps: readonly unknown[] = []): Resource<T> {
  const api = useApi();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    let live = true;
    setLoading(true);
    latest
      .current(api)
      .then((d) => {
        if (!live) return;
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [api, tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, error, loading, reload };
}

/** Unwrap an openapi-fetch result or throw its error body. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.data !== undefined) return result.data;
  const err = result.error as { message?: string; error?: string } | undefined;
  throw new Error(err?.message ?? err?.error ?? `HTTP ${result.response.status}`);
}
